import { useState, useRef, useEffect, Suspense } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import LoadingScreen from './LoadingScreen'
import { useAuth } from '../contexts/AuthContext'
import { useAuthModal } from '../contexts/AuthModalContext'
import { getSiteSettings, setSiteCurrency, CURRENCIES, detectCurrencyFromIp } from '../utils/siteSettings'
import { FiArrowRight, FiCalendar, FiSearch, FiKey, FiGift, FiGlobe, FiChevronDown, FiMenu, FiX, FiCompass, FiUser, FiHeadphones, FiHeart } from 'react-icons/fi'
import { FaXTwitter, FaInstagram, FaFacebookF, FaTiktok, FaPersonHiking } from 'react-icons/fa6'
import SearchBar from './SearchBar'
import './Layout.css'

const CURRENCY_FLAG_COUNTRY = {
  USD: 'us',
  EUR: 'eu',
  GBP: 'gb',
  CAD: 'ca',
  AUD: 'au',
  JPY: 'jp',
  CHF: 'ch',
  CNY: 'cn',
  INR: 'in',
  MXN: 'mx',
  BRL: 'br',
  KRW: 'kr',
  SGD: 'sg',
  THB: 'th',
  IDR: 'id',
  MYR: 'my',
  PHP: 'ph',
  NZD: 'nz',
  SEK: 'se',
  NOK: 'no',
  DKK: 'dk',
  ZAR: 'za',
  AED: 'ae',
  SAR: 'sa',
  TWD: 'tw',
  HKD: 'hk',
  TRY: 'tr',
  PLN: 'pl',
  CZK: 'cz',
  ILS: 'il',
  CLP: 'cl',
  COP: 'co',
  ARS: 'ar',
  PEN: 'pe',
  VND: 'vn',
  EGP: 'eg',
  QAR: 'qa',
  KWD: 'kw',
  BHD: 'bh',
  OMR: 'om',
}

const CURRENCY_PROMPT_DISMISS_KEY = 'voyabay_currency_prompt_dismissed'

const getCurrencyFlagUrl = (currency) => {
  const country = CURRENCY_FLAG_COUNTRY[String(currency || '').toUpperCase()]
  return country ? `https://flagcdn.com/w40/${country}.png` : ''
}

const readDismissedCurrencyPrompt = () => {
  try {
    return localStorage.getItem(CURRENCY_PROMPT_DISMISS_KEY) || ''
  } catch {
    return ''
  }
}

const dismissCurrencyPrompt = (currency) => {
  try {
    localStorage.setItem(CURRENCY_PROMPT_DISMISS_KEY, currency || '')
  } catch {}
}

function Layout() {
  const { user, logout } = useAuth()
  const { openLogin } = useAuthModal()
  const location = useLocation()
  const navigate = useNavigate()
  
  // World settings state
  const [showExplore, setShowExplore] = useState(false)
  const [showWorldSettings, setShowWorldSettings] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [displayCurrency, setDisplayCurrency] = useState(() => getSiteSettings().currency)
  const [selectedCurrency, setSelectedCurrency] = useState(displayCurrency)
  const [detectedCurrency, setDetectedCurrency] = useState('')
  const [dismissedCurrencyPrompt, setDismissedCurrencyPrompt] = useState(() => readDismissedCurrencyPrompt())
  const [detailSearchInfo, setDetailSearchInfo] = useState(null)
  const [rentalsSearchInfo, setRentalsSearchInfo] = useState(null)
  const activeCurrencyFlag = getCurrencyFlagUrl(displayCurrency)
  const detectedCurrencyFlag = getCurrencyFlagUrl(detectedCurrency)
  const shouldShowCurrencyPrompt = Boolean(
    detectedCurrency &&
    detectedCurrency !== displayCurrency &&
    dismissedCurrencyPrompt !== detectedCurrency
  )

  // Re-sync display language/currency when route changes (e.g. URL param updated settings)
  useEffect(() => {
    const { currency: c } = getSiteSettings()
    const urlCurrency = new URLSearchParams(location.search).get('currency')?.toUpperCase() || ''
    const currentCurrency = CURRENCIES.some(currency => currency.code === urlCurrency) ? urlCurrency : c
    setDisplayCurrency(currentCurrency)
    setSelectedCurrency(currentCurrency)
  }, [location.pathname, location.search])

  useEffect(() => {
    let cancelled = false
    detectCurrencyFromIp().then(currency => {
      if (cancelled || !currency) return
      setDetectedCurrency(currency)
    })
    return () => { cancelled = true }
  }, [])
  
  const searchParams = new URLSearchParams(location.search)
  const isAppEmbed = searchParams.get('source') === 'app'
  const isRentalsSearchPage = location.pathname === '/rentals'
  const isDetailsPage = location.pathname.startsWith('/hotel/') || location.pathname.startsWith('/rentals/')
  const [detailsHeaderScrolled, setDetailsHeaderScrolled] = useState(false)
  const rentalsDestination = rentalsSearchInfo?.name || rentalsSearchInfo?.label || searchParams.get('name') || searchParams.get('location') || ''
  const rentalsLocation = rentalsSearchInfo?.location || searchParams.get('location') || ''
  const rentalsCountry = rentalsSearchInfo?.country || searchParams.get('country') || ''
  const rentalsPlaceId = searchParams.get('placeId') || ''
  const rentalsLatitude = searchParams.get('latitude') || ''
  const rentalsLongitude = searchParams.get('longitude') || ''
  const rentalsCheckin = searchParams.get('checkin') || ''
  const rentalsCheckout = searchParams.get('checkout') || ''
  const rentalsAdults = Math.max(1, parseInt(searchParams.get('adults') || '2', 10) || 2)
  const rentalsChildAges = (searchParams.get('children') || '').split(',').map(Number).filter(n => Number.isFinite(n))
  const rentalsRooms = [{ adults: rentalsAdults, children: rentalsChildAges.length, childAges: rentalsChildAges }]
  const detailsPathParts = location.pathname.split('/').filter(Boolean)
  const detailsSlug = detailsPathParts.length > 2 ? detailsPathParts[2] : ''
  const detailsDestination = detailSearchInfo?.name || searchParams.get('name') || searchParams.get('location') || (detailsSlug ? detailsSlug.replace(/-/g, ' ') : 'Search')
  const detailsCheckin = searchParams.get('checkin') || rentalsCheckin
  const detailsCheckout = searchParams.get('checkout') || rentalsCheckout
  const detailsAdults = Math.max(1, parseInt(searchParams.get('adults') || '2', 10) || 2)
  const detailsRoomsCount = Math.max(1, parseInt(searchParams.get('rooms') || '1', 10) || 1)
  const formatHeaderDate = (value) => {
    if (!value) return ''
    const [year, month, day] = value.split('-').map(Number)
    if (!year || !month || !day) return ''
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  const detailsDateSummary = detailsCheckin && detailsCheckout
    ? `${formatHeaderDate(detailsCheckin)} - ${formatHeaderDate(detailsCheckout)}`
    : 'Add dates'
  const detailsGuestSummary = `${detailsRoomsCount} room${detailsRoomsCount > 1 ? 's' : ''}, ${detailsAdults} guest${detailsAdults > 1 ? 's' : ''}`
  const openDetailsSearch = () => {
    const params = new URLSearchParams()
    const searchName = detailSearchInfo?.name || detailsDestination || 'Nearby'
    const searchLocation = detailSearchInfo?.location || searchParams.get('location') || searchName
    const searchCountry = detailSearchInfo?.country || searchParams.get('country') || ''
    const latitude = detailSearchInfo?.latitude || searchParams.get('latitude') || ''
    const longitude = detailSearchInfo?.longitude || searchParams.get('longitude') || ''
    const currency = searchParams.get('currency') || displayCurrency

    params.set('name', searchName)
    if (searchLocation) params.set('location', searchLocation)
    if (searchCountry) params.set('country', searchCountry)
    if (latitude && longitude) {
      params.set('latitude', String(latitude))
      params.set('longitude', String(longitude))
    }
    if (detailsCheckin) params.set('checkin', detailsCheckin)
    if (detailsCheckout) params.set('checkout', detailsCheckout)
    params.set('rooms', String(detailsRoomsCount))
    params.set('adults', String(detailsAdults))
    // Preserve child ages from the current URL (comma-separated ages)
    const childrenAges = searchParams.get('children') || ''
    if (childrenAges) params.set('children', childrenAges)
    if (currency) params.set('currency', currency)

    if (location.pathname.startsWith('/rentals/')) params.set('vacationRentals', 'true')
    navigate(`/hotels?${params.toString()}`)
  }

  const exploreRef = useRef(null)
  const worldRef = useRef(null)

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exploreRef.current && !exploreRef.current.contains(e.target)) setShowExplore(false)
      if (worldRef.current && !worldRef.current.contains(e.target)) setShowWorldSettings(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

// Close mobile menu on route change
  useEffect(() => {
    setShowMobileMenu(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isDetailsPage) {
      setDetailsHeaderScrolled(false)
      setDetailSearchInfo(null)
      return
    }

    const handleScroll = () => {
      setDetailsHeaderScrolled(window.scrollY > 180)
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isDetailsPage])

  useEffect(() => {
    const handleDetailSearchInfo = (event) => {
      setDetailSearchInfo(event.detail || null)
    }

    window.addEventListener('voyabay:detail-search-info', handleDetailSearchInfo)
    return () => window.removeEventListener('voyabay:detail-search-info', handleDetailSearchInfo)
  }, [])

  useEffect(() => {
    if (!isRentalsSearchPage) {
      setRentalsSearchInfo(null)
      return
    }

    const handleRentalsSearchInfo = (event) => {
      setRentalsSearchInfo(event.detail || null)
    }

    window.addEventListener('voyabay:rentals-search-info', handleRentalsSearchInfo)
    return () => window.removeEventListener('voyabay:rentals-search-info', handleRentalsSearchInfo)
  }, [isRentalsSearchPage])

const handleCurrencyChange = (nextCurrency, closeMenus = false) => {
    setSelectedCurrency(nextCurrency)
    setSiteCurrency(nextCurrency, { userChosen: true })
    setDisplayCurrency(nextCurrency)
    const promptCurrency = detectedCurrency || nextCurrency
    dismissCurrencyPrompt(promptCurrency)
    setDismissedCurrencyPrompt(promptCurrency)

    const params = new URLSearchParams(location.search)
    params.set('currency', nextCurrency)
    if (nextCurrency !== 'USD') {
      params.delete('offerId')
      params.delete('roomName')
      params.delete('hintPrice')
    }

    const nextSearch = `?${params.toString()}`
    const isRatePage = (
      location.pathname === '/hotels' ||
      location.pathname === '/rentals' ||
      location.pathname.startsWith('/hotel/') ||
      location.pathname.startsWith('/rentals/')
    )

    if (isRatePage && typeof window !== 'undefined') {
      window.location.assign(`${location.pathname}${nextSearch}`)
    } else {
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true })
    }

    if (closeMenus) {
      setShowWorldSettings(false)
      setShowMobileMenu(false)
    }
  }

  const handleDismissCurrencyPrompt = () => {
    dismissCurrencyPrompt(detectedCurrency)
    setDismissedCurrencyPrompt(detectedCurrency)
  }

  return (
    <div className="layout">
      {!isAppEmbed && <header className={`header${isRentalsSearchPage ? ' rentals-header' : ''}${isDetailsPage ? ' details-mobile-header' : ''}${(detailsHeaderScrolled || (isDetailsPage && showMobileMenu)) ? ' details-mobile-header-scrolled' : ''}`}>
        <div className="header-content container">
          {/* Left: Logo + Explore */}
          <div className="header-left">
            <Link to="/" className="logo">
              <img src="/white-voyabay.png" alt="Voyabay" className="logo-image" />
            </Link>
            
            <div className="explore-dropdown desktop-only" ref={exploreRef}>
              <button className="explore-btn" onClick={() => setShowExplore(!showExplore)}>
                Explore
                <FiChevronDown size={14} />
              </button>
              {showExplore && (
                <div className="dropdown-menu explore-mega-menu">
                  <div className="explore-grid">
                    <div className="explore-continent">
                      <h4>North America</h4>
                      <Link to="/explore/new-york" onClick={() => setShowExplore(false)}>New York</Link>
                      <Link to="/explore/los-angeles" onClick={() => setShowExplore(false)}>Los Angeles</Link>
                      <Link to="/explore/las-vegas" onClick={() => setShowExplore(false)}>Las Vegas</Link>
                      <Link to="/explore/miami" onClick={() => setShowExplore(false)}>Miami</Link>
                      <Link to="/explore/orlando" onClick={() => setShowExplore(false)}>Orlando</Link>
                      <Link to="/explore/chicago" onClick={() => setShowExplore(false)}>Chicago</Link>
                      <Link to="/explore/san-francisco" onClick={() => setShowExplore(false)}>San Francisco</Link>
                      <Link to="/explore/washington-dc" onClick={() => setShowExplore(false)}>Washington DC</Link>
                      <Link to="/explore/seattle" onClick={() => setShowExplore(false)}>Seattle</Link>
                      <Link to="/explore/denver" onClick={() => setShowExplore(false)}>Denver</Link>
                      <Link to="/explore/honolulu" onClick={() => setShowExplore(false)}>Honolulu</Link>
                    </div>
                    <div className="explore-continent">
                      <h4>South America</h4>
                      <Link to="/explore/rio-de-janeiro" onClick={() => setShowExplore(false)}>Rio de Janeiro</Link>
                      <Link to="/explore/sao-paulo" onClick={() => setShowExplore(false)}>São Paulo</Link>
                      <Link to="/explore/buenos-aires" onClick={() => setShowExplore(false)}>Buenos Aires</Link>
                      <Link to="/explore/santiago" onClick={() => setShowExplore(false)}>Santiago</Link>
                      <Link to="/explore/lima" onClick={() => setShowExplore(false)}>Lima</Link>
                      <Link to="/explore/cusco" onClick={() => setShowExplore(false)}>Cusco</Link>
                      <Link to="/explore/bogota" onClick={() => setShowExplore(false)}>Bogotá</Link>
                      <Link to="/explore/cartagena" onClick={() => setShowExplore(false)}>Cartagena</Link>
                      <Link to="/explore/medellin" onClick={() => setShowExplore(false)}>Medellín</Link>
                      <Link to="/explore/panama-city" onClick={() => setShowExplore(false)}>Panama City</Link>
                      <Link to="/explore/san-juan" onClick={() => setShowExplore(false)}>San Juan</Link>
                      <Link to="/explore/punta-cana" onClick={() => setShowExplore(false)}>Punta Cana</Link>
                    </div>
                    <div className="explore-continent">
                      <h4>Europe</h4>
                      <Link to="/explore/paris" onClick={() => setShowExplore(false)}>Paris</Link>
                      <Link to="/explore/london" onClick={() => setShowExplore(false)}>London</Link>
                      <Link to="/explore/rome" onClick={() => setShowExplore(false)}>Rome</Link>
                      <Link to="/explore/barcelona" onClick={() => setShowExplore(false)}>Barcelona</Link>
                      <Link to="/explore/amsterdam" onClick={() => setShowExplore(false)}>Amsterdam</Link>
                      <Link to="/explore/prague" onClick={() => setShowExplore(false)}>Prague</Link>
                      <Link to="/explore/istanbul" onClick={() => setShowExplore(false)}>Istanbul</Link>
                      <Link to="/explore/vienna" onClick={() => setShowExplore(false)}>Vienna</Link>
                      <Link to="/explore/berlin" onClick={() => setShowExplore(false)}>Berlin</Link>
                      <Link to="/explore/madrid" onClick={() => setShowExplore(false)}>Madrid</Link>
                      <Link to="/explore/lisbon" onClick={() => setShowExplore(false)}>Lisbon</Link>
                      <Link to="/explore/dublin" onClick={() => setShowExplore(false)}>Dublin</Link>
                    </div>
                    <div className="explore-continent">
                      <h4>Asia</h4>
                      <Link to="/explore/tokyo" onClick={() => setShowExplore(false)}>Tokyo</Link>
                      <Link to="/explore/bangkok" onClick={() => setShowExplore(false)}>Bangkok</Link>
                      <Link to="/explore/singapore" onClick={() => setShowExplore(false)}>Singapore</Link>
                      <Link to="/explore/dubai" onClick={() => setShowExplore(false)}>Dubai</Link>
                      <Link to="/explore/bali" onClick={() => setShowExplore(false)}>Bali</Link>
                      <Link to="/explore/hong-kong" onClick={() => setShowExplore(false)}>Hong Kong</Link>
                      <Link to="/explore/seoul" onClick={() => setShowExplore(false)}>Seoul</Link>
                      <Link to="/explore/osaka" onClick={() => setShowExplore(false)}>Osaka</Link>
                      <Link to="/explore/phuket" onClick={() => setShowExplore(false)}>Phuket</Link>
                      <Link to="/explore/shanghai" onClick={() => setShowExplore(false)}>Shanghai</Link>
                      <Link to="/explore/kuala-lumpur" onClick={() => setShowExplore(false)}>Kuala Lumpur</Link>
                      <Link to="/explore/delhi" onClick={() => setShowExplore(false)}>Delhi</Link>
                    </div>
                    <div className="explore-continent">
                      <h4>Africa</h4>
                      <Link to="/explore/cape-town" onClick={() => setShowExplore(false)}>Cape Town</Link>
                      <Link to="/explore/marrakech" onClick={() => setShowExplore(false)}>Marrakech</Link>
                      <Link to="/explore/cairo" onClick={() => setShowExplore(false)}>Cairo</Link>
                      <Link to="/explore/nairobi" onClick={() => setShowExplore(false)}>Nairobi</Link>
                      <Link to="/explore/zanzibar" onClick={() => setShowExplore(false)}>Zanzibar</Link>
                      <Link to="/explore/johannesburg" onClick={() => setShowExplore(false)}>Johannesburg</Link>
                      <Link to="/explore/casablanca" onClick={() => setShowExplore(false)}>Casablanca</Link>
                      <Link to="/explore/sharm-el-sheikh" onClick={() => setShowExplore(false)}>Sharm El-Sheikh</Link>
                      <Link to="/explore/accra" onClick={() => setShowExplore(false)}>Accra</Link>
                      <Link to="/explore/kigali" onClick={() => setShowExplore(false)}>Kigali</Link>
                    </div>
                    <div className="explore-continent">
                      <h4>Oceania</h4>
                      <Link to="/explore/sydney" onClick={() => setShowExplore(false)}>Sydney</Link>
                      <Link to="/explore/melbourne" onClick={() => setShowExplore(false)}>Melbourne</Link>
                      <Link to="/explore/auckland" onClick={() => setShowExplore(false)}>Auckland</Link>
                      <Link to="/explore/queenstown" onClick={() => setShowExplore(false)}>Queenstown</Link>
                      <Link to="/explore/fiji" onClick={() => setShowExplore(false)}>Fiji</Link>
                      <Link to="/explore/brisbane" onClick={() => setShowExplore(false)}>Brisbane</Link>
                      <Link to="/explore/perth" onClick={() => setShowExplore(false)}>Perth</Link>
                      <Link to="/explore/cairns" onClick={() => setShowExplore(false)}>Cairns</Link>
                      <Link to="/explore/wellington" onClick={() => setShowExplore(false)}>Wellington</Link>
                      <Link to="/explore/bora-bora" onClick={() => setShowExplore(false)}>Bora Bora</Link>
                    </div>
                  </div>
                  <div className="explore-footer">
                    <Link to="/explore" className="view-all-link" onClick={() => setShowExplore(false)}>
                      View all 120+ destinations →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isDetailsPage && (
            <div className="details-header-search mobile-only">
              <button className="details-header-search-pill" onClick={openDetailsSearch}>
                <span className="details-header-search-destination">{detailsDestination}</span>
                <span className="details-header-search-meta">{detailsDateSummary} · {detailsGuestSummary}</span>
              </button>
              <button className="details-header-search-btn" onClick={openDetailsSearch} aria-label="Edit search">
                <FiSearch size={17} />
              </button>
            </div>
          )}

          {isRentalsSearchPage && (
            <div className="header-rentals-search-mobile mobile-only">
              <SearchBar
                initialDestination={rentalsDestination}
                initialLocation={rentalsLocation}
                initialCountry={rentalsCountry}
                initialPlaceId={rentalsPlaceId}
                initialLatitude={rentalsLatitude}
                initialLongitude={rentalsLongitude}
                initialCheckin={rentalsCheckin}
                initialCheckout={rentalsCheckout}
                initialRooms={rentalsRooms}
                vacationRentals
                className="layout-rentals-header-search-mobile"
              />
            </div>
          )}

          {/* Right: Nav Links + World Settings + Auth (Desktop) */}
          <div className="header-right desktop-only">
            <nav className="nav">
              <Link
                to="/stays"
                className={`nav-link ${location.pathname === '/stays' ? 'active' : ''}`}
                onClick={(e) => {
                  if (!user) {
                    e.preventDefault()
                    sessionStorage.setItem('voyabay_post_login_redirect', '/stays')
                    openLogin()
                  }
                }}
              >
                Stays
              </Link>
              <a href="https://experiences.voyabay.com" className="nav-link" target="_blank" rel="noopener noreferrer">Experiences</a>
              <Link to="/favorites" className={`nav-link ${location.pathname === '/favorites' ? 'active' : ''}`}>Favorites</Link>
              <Link to="/support" className={`nav-link ${location.pathname === '/support' ? 'active' : ''}`}>Support</Link>
              <Link to="/account" className={`nav-link ${location.pathname === '/account' ? 'active' : ''}`}>Account</Link>
            </nav>

            {/* World Settings Button */}
            <div className="world-settings" ref={worldRef}>
              <button className="world-btn" onClick={() => setShowWorldSettings(!showWorldSettings)}>
                {activeCurrencyFlag ? (
                  <img src={activeCurrencyFlag} alt="" className="currency-flag" />
                ) : (
                  <FiGlobe size={16} />
                )}
                {displayCurrency}
              </button>
              
              {showWorldSettings && (
                <div className="dropdown-menu world-menu">
                  <div className="world-section">
                    <label>Currency</label>
                    <select value={selectedCurrency} onChange={(e) => handleCurrencyChange(e.target.value, true)}>
                      {CURRENCIES.map(c => (
                        <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {shouldShowCurrencyPrompt && (
                <div className="currency-switch-prompt currency-switch-prompt-desktop" role="dialog" aria-live="polite">
                  <button
                    type="button"
                    className="currency-switch-dismiss"
                    onClick={handleDismissCurrencyPrompt}
                    aria-label="Dismiss currency suggestion"
                  >
                    <FiX size={15} />
                  </button>
                  <div className="currency-switch-copy">
                    <span className="currency-switch-kicker">Local currency detected</span>
                    <strong>Switch to {detectedCurrency}?</strong>
                  </div>
                  <button
                    type="button"
                    className="currency-switch-action"
                    onClick={() => handleCurrencyChange(detectedCurrency)}
                  >
                    {detectedCurrencyFlag && <img src={detectedCurrencyFlag} alt="" className="currency-flag" />}
                    <span>{detectedCurrency}</span>
                    <FiArrowRight size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Auth */}
            {user ? (
              <button className="signout-btn" onClick={logout}>Sign out</button>
            ) : (
              <button className="signin-btn" onClick={openLogin}>Sign in</button>
            )}

          </div>

          {/* Mobile: Hamburger Menu Button */}
          <button className="hamburger-btn mobile-only" onClick={() => setShowMobileMenu(!showMobileMenu)}>
            {showMobileMenu ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {showMobileMenu && (
          <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
            <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
              <nav className="mobile-nav">
                <Link
                  to="/stays"
                  className={`mobile-nav-link ${location.pathname === '/stays' ? 'active' : ''}`}
                  onClick={(e) => {
                    if (!user) {
                      e.preventDefault()
                      sessionStorage.setItem('voyabay_post_login_redirect', '/stays')
                      openLogin()
                    }
                    setShowMobileMenu(false)
                  }}
                >
                  <FiCalendar size={20} />
                  My Stays
                </Link>
                <a href="https://experiences.voyabay.com" className="mobile-nav-link" target="_blank" rel="noopener noreferrer">
                  <FaPersonHiking size={20} />
                  Experiences
                </a>
                <Link to="/explore" className={`mobile-nav-link ${location.pathname.startsWith('/explore') ? 'active' : ''}`} onClick={() => setShowMobileMenu(false)}>
                  <FiCompass size={20} />
                  Explore Destinations
                </Link>
                <Link to="/favorites" className={`mobile-nav-link ${location.pathname === '/favorites' ? 'active' : ''}`} onClick={() => setShowMobileMenu(false)}>
                  <FiHeart size={20} />
                  Favorites
                </Link>
                <Link to="/support" className={`mobile-nav-link ${location.pathname === '/support' ? 'active' : ''}`} onClick={() => setShowMobileMenu(false)}>
                  <FiHeadphones size={20} />
                  Support
                </Link>
                <Link to="/account" className={`mobile-nav-link ${location.pathname === '/account' ? 'active' : ''}`}>
                  <FiUser size={20} />
                  Account
                </Link>
              </nav>

              <div className="mobile-menu-divider"></div>

              {/* World Settings in Mobile Menu */}
              <div className="mobile-world-settings">
                <h4>Settings</h4>
                <div className="mobile-world-section">
                  <label>Currency</label>
                  <select value={selectedCurrency} onChange={(e) => handleCurrencyChange(e.target.value, true)}>
                    {CURRENCIES.map(c => (
                      <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mobile-menu-divider"></div>

              {/* Auth Button */}
              <div className="mobile-auth">
                {user ? (
                  <button className="mobile-signout-btn" onClick={() => { logout(); setShowMobileMenu(false); }}>
                    Sign out
                  </button>
                ) : (
                  <button className="mobile-signin-btn" onClick={() => { openLogin(); setShowMobileMenu(false); }}>
                    Sign in
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>}

      <main className="main">
        <Suspense fallback={<LoadingScreen fullScreen={false} />}>
          <Outlet />
        </Suspense>
      </main>

      {isDetailsPage && !isAppEmbed && (
        <section className="details-card-banner desktop-only">
          <div className="details-card-banner-inner">
            <img src="/voyabaycard.png" alt="Voyabay Card" className="details-card-banner-img" />
            <div className="details-card-banner-body">
              <p className="details-card-banner-heading">
                <span className="details-card-banner-crossed">Annual Fees</span>{' '}
                <span className="details-card-banner-highlight">Annual Bonus</span>
              </p>
              <ul className="details-card-banner-perks">
                <li>Earn 1.5% back on all purchases</li>
                <li>No credit check</li>
              </ul>
            </div>
            <a href="/card" target="_blank" rel="noopener noreferrer" className="details-card-banner-cta">
              Learn more <FiArrowRight size={15} />
            </a>
          </div>
        </section>
      )}

      {!isAppEmbed && shouldShowCurrencyPrompt && (
        <div className="currency-switch-prompt currency-switch-prompt-mobile" role="dialog" aria-live="polite">
          <button
            type="button"
            className="currency-switch-dismiss"
            onClick={handleDismissCurrencyPrompt}
            aria-label="Dismiss currency suggestion"
          >
            <FiX size={16} />
          </button>
          <button
            type="button"
            className="currency-switch-mobile-action"
            onClick={() => handleCurrencyChange(detectedCurrency)}
            aria-label={`Switch to ${detectedCurrency}`}
          >
            <span className="currency-switch-mobile-icon">
              {detectedCurrencyFlag && <img src={detectedCurrencyFlag} alt="" className="currency-flag" />}
            </span>
            <span className="currency-switch-mobile-copy">
              <span>Switch to local currency</span>
              <strong>{detectedCurrency}</strong>
            </span>
            <span className="currency-switch-mobile-cta">
              Switch
            </span>
          </button>
        </div>
      )}

      {!isAppEmbed && <footer className={`footer${isRentalsSearchPage ? ' footer-hide-mobile' : ''}${isDetailsPage ? ' footer-details-page' : ''}`}>
        <div className="footer-content container">
          <div className="footer-top">
            <div className="footer-brand">
              <img src="/white-voyabay.png" alt="Voyabay" className="footer-logo-image" />
              <span className="footer-tagline">Stay anywhere. Do everything.</span>
              <span className="footer-address">28 Sackville St, Boston MA 02129</span>
            </div>
            <div className="footer-trust-social">
              <div className="footer-socials">
                <a href="https://x.com/voyabay" target="_blank" rel="noopener noreferrer" aria-label="Twitter/X"><FaXTwitter size={18} /></a>
                <a href="https://instagram.com/voyabay" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FaInstagram size={18} /></a>
                <a href="https://facebook.com/voyabay" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FaFacebookF size={18} /></a>
                <a href="https://tiktok.com/@voyabay" target="_blank" rel="noopener noreferrer" aria-label="TikTok"><FaTiktok size={18} /></a>
              </div>
              <div className="footer-trust-signals">
                <div className="footer-trust-signal"><FiGlobe size={14} /><span>180+ Countries</span></div>
                <div className="footer-trust-signal"><FiHeadphones size={14} /><span>24/7 Customer Support</span></div>
                <div className="footer-trust-signal"><FaPersonHiking size={14} /><span>400,000+ Experiences</span></div>
              </div>
              <div className="footer-trending">
                <span className="footer-trending-label">Trending:</span>
                <Link to="/explore/ho-chi-minh-city">Ho Chi Minh</Link>
                <Link to="/explore/london">London</Link>
                <Link to="/explore/orlando">Orlando</Link>
                <Link to="/explore/madrid">Madrid</Link>
                <Link to="/explore/rome">Rome</Link>
                <Link to="/explore/las-vegas">Las Vegas</Link>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-links">
              <Link to="/terms">Terms & Conditions</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/cookies">Cookie Policy</Link>
              <Link to="/support">Contact Us</Link>
              <Link to="/jobs">Jobs</Link>
              <Link to="/about">About Us</Link>
              <Link to="/partnerships">Partnerships</Link>
            </div>
            <span className="footer-copyright">© 2025 Voyabay. All rights reserved.</span>
          </div>
        </div>
      </footer>}
    </div>
  )
}

export default Layout
